import React, { useEffect, useState } from 'react';
import { 
    Table, 
    Button, 
    Tag, 
    Space, 
    Modal, 
    Form, 
    Input, 
    Select, 
    InputNumber, 
    Switch, 
    Alert, 
    Typography, 
    Card, 
    Row, 
    Col, 
    Statistic, 
    Progress, 
    Tooltip, 
    Popconfirm,
    message,
    Badge,
    Avatar,
    Image,
    Divider,
    List,
    Descriptions,
    Collapse
} from 'antd';
import { 
    CheckCircleOutlined, 
    CloseCircleOutlined, 
    EyeOutlined, 
    EditOutlined, 
    DeleteOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    FilterOutlined,
    SearchOutlined,
    ReloadOutlined,
    FileTextOutlined,
    UserOutlined,
    ShopOutlined,
    DollarOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import api from '../../../services/api';
import { getStatusColor, formatCurrency } from '../../../utils/ui';
import AIDescriptionGenerator from '../../UI/AIDescriptionGenerator';
import { resolveImageSrc } from '../../../utils/images';
import ModernImageUpload from '../../UI/ModernImageUpload';
import MultipleImageUpload from '../../UI/MultipleImageUpload';

const { Title, Paragraph, Text } = Typography;
const { Search } = Input;

function AdminMenus() {
    const [menus, setMenus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedMenu, setSelectedMenu] = useState(null);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [categories, setCategories] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [editForm] = Form.useForm();
    const [editSaving, setEditSaving] = useState(false);
    const [filters, setFilters] = useState({
        status: 'all',
        category: 'all',
        vendor: 'all',
        search: ''
    });
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0
    });

    const load = async () => {
        try {
            setLoading(true);
            const res = await api.adminListMenus({ per_page: 100 });
            const menuData = res.data.data || [];
            setMenus(menuData);
            
            // Calculate stats
            const statsData = {
                total: menuData.length,
                pending: menuData.filter(m => m.status === 'pending').length,
                approved: menuData.filter(m => m.status === 'approved').length,
                rejected: menuData.filter(m => m.status === 'rejected').length
            };
            setStats(statsData);
        } catch (e) {
            setError('Failed to load menus');
            message.error('Failed to load menus');
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const res = await api.getCategories();
            setCategories(res.data || []);
        } catch (e) {
            console.error('Failed to load categories');
            setCategories([]);
        }
    };

    const loadVendors = async () => {
        try {
            const res = await api.getVendors({ per_page: 100 });
            setVendors(res.data.data || []);
        } catch (e) {
            console.error('Failed to load vendors');
            setVendors([]);
        }
    };

    useEffect(() => { 
        load(); 
        loadCategories();
        loadVendors();
    }, []);

    const approve = async (id) => {
        try {
            await api.adminApproveMenu(id);
            message.success('Menu approved successfully');
            await load();
        } catch (e) {
            message.error('Failed to approve menu');
        }
    };

    const reject = async (id) => {
        try {
            await api.adminRejectMenu(id);
            message.success('Menu rejected successfully');
            await load();
        } catch (e) {
            message.error('Failed to reject menu');
        }
    };

    const deleteMenu = async (id) => {
        try {
            await api.adminDeleteMenu(id);
            message.success('Menu deleted successfully');
            await load();
        } catch (e) {
            message.error('Failed to delete menu');
        }
    };

    const viewMenu = (menu) => {
        setSelectedMenu(menu);
        setViewModalVisible(true);
    };

    const editMenu = (menu) => {
        setSelectedMenu(menu);
        setEditModalVisible(true);
        
        // Convert existing images to the format expected by MultipleImageUpload
        const existingImages = menu.images ? menu.images.map(img => ({
            url: img.url || img,
            thumb: img.thumb,
            medium: img.medium,
            large: img.large,
            id: img.id,
            preview: img.url || img.thumb || img
        })) : (menu.image ? [{
            url: menu.image,
            thumb: menu.imageVariants?.thumb,
            medium: menu.imageVariants?.medium,
            large: menu.imageVariants?.large,
            id: menu.image_id,
            preview: menu.image
        }] : []);
        
        // prime form values
        editForm.setFieldsValue({
            name: menu.name,
            description: menu.description,
            price: menu.price,
            status: menu.status || (menu.is_available ? 'approved' : 'pending'),
            category_id: menu.category?.id,
            preparation_time: menu.preparation_time,
            spice_level: menu.spice_level,
            is_vegetarian: !!menu.is_vegetarian,
            is_vegan: !!menu.is_vegan,
            is_gluten_free: !!menu.is_gluten_free,
            is_spicy: !!menu.is_spicy,
            ingredients: Array.isArray(menu.ingredients) ? menu.ingredients : [],
            images: existingImages
        });
    };

    const getStatusTag = (status) => {
        const normalized = (status || 'pending').toLowerCase();
        const color = getStatusColor(normalized === 'approved' ? 'success' : normalized);
        const iconMap = {
            pending: <ClockCircleOutlined />,
            approved: <CheckCircleOutlined />,
            rejected: <CloseCircleOutlined />,
            draft: <FileTextOutlined />,
        };
        const textMap = {
            pending: 'Pending Review',
            approved: 'Approved',
            rejected: 'Rejected',
            draft: 'Draft',
        };
        return (
            <Tag color={color} icon={iconMap[normalized] || <ClockCircleOutlined />}>
                {textMap[normalized] || 'Pending Review'}
            </Tag>
        );
    };

    const getFilteredMenus = () => {
        let filtered = menus;
        
        if (filters.status !== 'all') {
            filtered = filtered.filter(m => m.status === filters.status);
        }
        
        if (filters.category !== 'all') {
            filtered = filtered.filter(m => m.category?.id === parseInt(filters.category));
        }
        
        if (filters.vendor !== 'all') {
            filtered = filtered.filter(m => m.vendor?.id === parseInt(filters.vendor));
        }
        
        if (filters.search) {
            filtered = filtered.filter(m => 
                m.name.toLowerCase().includes(filters.search.toLowerCase()) ||
                m.vendor?.business_name?.toLowerCase().includes(filters.search.toLowerCase())
            );
        }
        
        return filtered;
    };

    const columns = [
        {
            title: 'Menu',
            key: 'menu',
            render: (_, record) => (
                <div className="flex items-center space-x-3">
                    <Avatar 
                        size={48} 
                        src={resolveImageSrc((record.imageVariants && (record.imageVariants.thumb || record.imageVariants.medium)) || record.image)} 
                        icon={<FileTextOutlined />}
                        className="bg-indigo-100 text-indigo-600"
                    />
                    <div>
                        <div className="font-medium text-gray-900">{record.name}</div>
                        <div className="text-sm text-gray-500">{record.description}</div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Vendor',
            key: 'vendor',
            render: (_, record) => (
                <div className="flex items-center space-x-2">
                    <Avatar size={32} icon={<ShopOutlined />} />
                    <div>
                        <div className="font-medium">{record.vendor?.business_name}</div>
                        <div className="text-sm text-gray-500">{record.vendor?.city?.name}</div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Category',
            key: 'category',
            render: (_, record) => (
                <Tag color="blue">{record.category?.name}</Tag>
            ),
        },
        {
            title: 'Price',
            key: 'price',
            render: (_, record) => (
                <div className="text-right">
                    <div className="font-medium text-green-600">{formatCurrency(record.price)}</div>
                    <div className="text-sm text-gray-500">per person</div>
                </div>
            ),
        },
        {
            title: 'Portions/Person',
            key: 'portions',
            render: (_, record) => (
                <div className="text-center">
                    <div className="font-medium">{record.portions_per_person || 0.15}</div>
                    <div className="text-sm text-gray-500">portions</div>
                </div>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => getStatusTag(record.status || 'pending'),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button 
                            type="text" 
                            icon={<EyeOutlined />} 
                            onClick={() => viewMenu(record)}
                            size="small"
                        />
                    </Tooltip>
                    
                    {record.status === 'pending' && (
                        <>
                            <Tooltip title="Approve Menu">
                                <Button 
                                    type="primary" 
                                    icon={<CheckCircleOutlined />} 
                                    onClick={() => approve(record.id)}
                                    size="small"
                                    className="bg-green-600 hover:bg-green-700"
                                >
                                    Approve
                                </Button>
                            </Tooltip>
                            <Tooltip title="Reject Menu">
                                <Button 
                                    danger 
                                    icon={<CloseCircleOutlined />} 
                                    onClick={() => reject(record.id)}
                                    size="small"
                                >
                                    Reject
                                </Button>
                            </Tooltip>
                        </>
                    )}
                    
                    <Tooltip title="Edit Menu">
                        <Button 
                            type="text" 
                            icon={<EditOutlined />} 
                            onClick={() => editMenu(record)}
                            size="small"
                        />
                    </Tooltip>
                    
                    <Popconfirm
                        title="Delete Menu"
                        description="Are you sure you want to delete this menu? This action cannot be undone."
                        onConfirm={() => deleteMenu(record.id)}
                        okText="Yes, Delete"
                        cancelText="Cancel"
                    >
                        <Tooltip title="Delete Menu">
                            <Button 
                                type="text" 
                                danger 
                                icon={<DeleteOutlined />} 
                                size="small"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="mb-6">
                <Title level={2}>Menu Moderation</Title>
                <Paragraph type="secondary">
                    Review, approve, reject, and manage all vendor menus on the platform.
                </Paragraph>
            </div>

            {/* Statistics Cards */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Total Menus"
                            value={stats.total}
                            prefix={<FileTextOutlined />}
                            valueStyle={{ color: '#3f51b5' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Pending Review"
                            value={stats.pending}
                            prefix={<ClockCircleOutlined />}
                            valueStyle={{ color: '#ff9800' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Approved"
                            value={stats.approved}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#4caf50' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Rejected"
                            value={stats.rejected}
                            prefix={<CloseCircleOutlined />}
                            valueStyle={{ color: '#f44336' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Progress Bar */}
            {stats.total > 0 && (
                <Card className="mb-6">
                    <div className="mb-4">
                        <Text strong>Moderation Progress</Text>
                    </div>
                    <Progress
                        percent={Math.round(((stats.approved + stats.rejected) / stats.total) * 100)}
                        status="active"
                        strokeColor={{
                            '0%': '#108ee9',
                            '100%': '#87d068',
                        }}
                    />
                    <div className="mt-2 text-sm text-gray-500">
                        {stats.approved + stats.rejected} of {stats.total} menus processed
                    </div>
                </Card>
            )}

            {/* Filters */}
            <Card className="mb-6">
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={6}>
                        <Search
                            placeholder="Search menus..."
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            prefix={<SearchOutlined />}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Select
                            placeholder="Status"
                            value={filters.status}
                            onChange={(value) => setFilters({ ...filters, status: value })}
                            style={{ width: '100%' }}
                        >
                            <Select.Option value="all">All Status</Select.Option>
                            <Select.Option value="pending">Pending</Select.Option>
                            <Select.Option value="approved">Approved</Select.Option>
                            <Select.Option value="rejected">Rejected</Select.Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Select
                            placeholder="Category"
                            value={filters.category}
                            onChange={(value) => setFilters({ ...filters, category: value })}
                            style={{ width: '100%' }}
                        >
                            <Select.Option value="all">All Categories</Select.Option>
                            {(categories || []).map(cat => (
                                <Select.Option key={cat.id} value={cat.id}>{cat.name}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Select
                            placeholder="Vendor"
                            value={filters.vendor}
                            onChange={(value) => setFilters({ ...filters, vendor: value })}
                            style={{ width: '100%' }}
                        >
                            <Select.Option value="all">All Vendors</Select.Option>
                            {(vendors || []).map(vendor => (
                                <Select.Option key={vendor.id} value={vendor.id}>{vendor.business_name}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Space>
                            <Button 
                                icon={<ReloadOutlined />} 
                                onClick={load}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                            <Button 
                                icon={<FilterOutlined />} 
                                onClick={() => setFilters({ status: 'all', category: 'all', vendor: 'all', search: '' })}
                            >
                                Clear Filters
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Error Alert */}
            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    className="mb-6"
                    closable
                    onClose={() => setError('')}
                />
            )}

            {/* Menus Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={getFilteredMenus()}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} menus`,
                    }}
                    scroll={{ x: 1200 }}
                />
            </Card>

            {/* View Menu Modal */}
            <Modal
                title="Menu Details"
                open={viewModalVisible}
                onCancel={() => setViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setViewModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                {selectedMenu && (
                    <div>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Image
                                    width={200}
                                    src={resolveImageSrc((selectedMenu.imageVariants && (selectedMenu.imageVariants.medium || selectedMenu.imageVariants.large)) || selectedMenu.image || '/images/placeholder.svg')}
                                    fallback="/images/placeholder.svg"
                                    alt={selectedMenu.name}
                                />
                            </Col>
                            <Col span={12}>
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="Menu Name">{selectedMenu.name}</Descriptions.Item>
                                    <Descriptions.Item label="Vendor">{selectedMenu.vendor?.business_name}</Descriptions.Item>
                                    <Descriptions.Item label="Category">{selectedMenu.category?.name}</Descriptions.Item>
                                    <Descriptions.Item label="Price">₹{selectedMenu.price}</Descriptions.Item>
                                    <Descriptions.Item label="Status">{getStatusTag(selectedMenu.status)}</Descriptions.Item>
                                    <Descriptions.Item label="Description">{selectedMenu.description}</Descriptions.Item>
                                </Descriptions>
                            </Col>
                        </Row>
                    </div>
                )}
            </Modal>

            {/* Edit Menu Modal */}
            <Modal
                title="Edit Menu"
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setEditModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button key="submit" type="primary" loading={editSaving} onClick={() => editForm.submit()}>
                        Save Changes
                    </Button>
                ]}
                width={600}
            >
                {selectedMenu && (
                    <Form form={editForm} layout="vertical" onFinish={async (values) => {
                        try {
                            setEditSaving(true);
                            
                            // Handle multiple images
                            const formData = new FormData();
                            formData.append('name', values.name);
                            formData.append('description', values.description || '');
                            formData.append('price', values.price);
                            formData.append('status', values.status);
                            formData.append('category_id', values.category_id);
                            formData.append('preparation_time', values.preparation_time);
                            formData.append('spice_level', values.spice_level);
                            formData.append('is_vegetarian', values.is_vegetarian ? '1' : '0');
                            formData.append('is_vegan', values.is_vegan ? '1' : '0');
                            formData.append('is_gluten_free', values.is_gluten_free ? '1' : '0');
                            formData.append('is_spicy', values.is_spicy ? '1' : '0');
                            formData.append('ingredients', JSON.stringify(values.ingredients || []));
                            
                            // Add multiple images to FormData
                            if (values.images && values.images.length > 0) {
                                values.images.forEach((imageData, index) => {
                                    if (imageData && imageData.file) {
                                        formData.append(`images[${index}]`, imageData.file);
                                    }
                                });
                            }
                            
                            await api.adminUpdateMenuWithFiles(selectedMenu.id, formData);
                            message.success('Menu updated');
                            setEditModalVisible(false);
                            await load();
                        } catch (e) {
                            message.error(e.message || 'Failed to update menu');
                        } finally { setEditSaving(false); }
                    }}>
                        <Form.Item style={{ display: 'none' }}>
                            <input type="text" />
                        </Form.Item>
                        <Form.Item label="Menu Images" name="images">
                            <MultipleImageUpload
                                maxFiles={10}
                            />
                        </Form.Item>
                        <Form.Item label="Menu Name" name="name" rules={[{ required: true, message: 'Please enter menu name' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="Description" name="description">
                            <Input.TextArea rows={3} />
                        </Form.Item>
                        
                        <Collapse 
                            items={[{
                                key: 'ai-description',
                                label: '🤖 AI Description Generator',
                                children: (
                                    <AIDescriptionGenerator
                                        menuItem={form.getFieldsValue()}
                                        onDescriptionGenerated={(description) => {
                                            form.setFieldsValue({ description });
                                        }}
                                        placeholder="Enter additional details about this dish to generate a compelling description..."
                                    />
                                )
                            }]}
                            className="mb-4"
                        />
                        <Row gutter={[12,12]}>
                            <Col span={12}>
                                <Form.Item label="Price" name="price" rules={[{ required: true, message: 'Enter price' }]}>
                                    <InputNumber
                                        min={0}
                                        formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={value => value.replace(/\₹\s?|(,*)/g, '')}
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Portions per Person" name="portions_per_person" rules={[{ required: true, message: 'Enter portions per person' }]}>
                                    <InputNumber
                                        min={0.01}
                                        max={1.0}
                                        step={0.01}
                                        placeholder="0.15"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[12,12]}>
                            <Col span={12}>
                                <Form.Item label="Category" name="category_id" rules={[{ required: true, message: 'Select category' }]}>
                                    <Select placeholder="Select category">
                                        {(categories || []).map(cat => (
                                            <Select.Option key={cat.id} value={cat.id}>{cat.name}</Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Preparation Time (mins)" name="preparation_time">
                                    <InputNumber min={0} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[12,12]}>
                            <Col span={12}>
                                <Form.Item label="Spice Level" name="spice_level">
                                    <InputNumber min={0} max={5} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Ingredients" name="ingredients">
                                    <Select mode="tags" placeholder="Add ingredients" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[12,12]}>
                            <Col span={6}><Form.Item name="is_vegetarian" valuePropName="checked" label="Vegetarian"><Switch /></Form.Item></Col>
                            <Col span={6}><Form.Item name="is_vegan" valuePropName="checked" label="Vegan"><Switch /></Form.Item></Col>
                            <Col span={6}><Form.Item name="is_gluten_free" valuePropName="checked" label="Gluten Free"><Switch /></Form.Item></Col>
                            <Col span={6}><Form.Item name="is_spicy" valuePropName="checked" label="Spicy"><Switch /></Form.Item></Col>
                        </Row>
                        <Form.Item label="Status" name="status">
                            <Select>
                                <Select.Option value="pending">Pending</Select.Option>
                                <Select.Option value="approved">Approved</Select.Option>
                                <Select.Option value="rejected">Rejected</Select.Option>
                            </Select>
                        </Form.Item>
                    </Form>
                )}
            </Modal>
        </div>
    );
}

export default AdminMenus;
